import {useContext, useEffect, useState} from 'react'
import {Col, Flex, Form, Modal, Row} from 'antd'
import {useIntl} from 'react-intl'
import {getRequest, postRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {notifyError} from '../../../../utils/notifications'
import {CAN_CHANGE_ROLE} from '../../../../constants/permission'
import {SET_SHOW_USER_POPUP, UserContext} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'
import {getLocalStorageData} from '../../../../utils/LocalStorageAccess'
import {MAKE_ID} from '../../../../constants/common'

const UserFormModal = (props) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(UserContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()

  const [roleOptions, setRoleOptions] = useState([])
  const [makeOptions, setMakeOptions] = useState([])

  const selected = state?.selected_user || {}
  const canChangeRole = navState?.permissions?.includes(CAN_CHANGE_ROLE)
  const localStorageMake = getLocalStorageData(MAKE_ID)

  const closeFormPopup = () => {
    dispatch({ type: SET_SHOW_USER_POPUP, data: false })
    form.resetFields()
  }

  const getInitialValues = () => ({
    is_create: !selected?.email,
    is_active: selected?.is_active ?? true,
    email: selected?.email || '',
    first_name: selected?.first_name || '',
    last_name: selected?.last_name || '',
    password: '',
    role: selected?.role || [],
  })

  const fetchRoles = async () => {
    let res = await getRequest(`/api/role/`)
    // Loaded once when the form opens, not a search, so a toast is right here.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'generic.rolesLoadError' }))
      return
    }
    const results = res?.results || []
    setRoleOptions(
      results.map((item) => ({ value: item.id, label: item.name }))
    )
  }

  const fetchMakes = async () => {
    let res = await getRequest(`/api/users/make_options/`)
    // Loaded once when the form opens, not a search, so a toast is right here.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'generic.makesLoadError' }))
      return
    }
    const results = res?.results || []
    setMakeOptions(
      results.map((item) => ({ value: item.id, label: item.name }))
    )
  }

  useEffect(() => {
    if (!state.show_user_popup) return
    fetchRoles()
    // Only needed when creating a user for a specific customer (no global make)
    if (!state?.selected_user?.email) {
      if (localStorageMake || navState?.make) {
        const make = localStorageMake || navState?.make
        setMakeOptions([{ value: make, label: make }])
      } else {
        fetchMakes()
      }
    }
  }, [state.show_user_popup])

  useEffect(() => {
    if (!state.show_user_popup) return
    form.setFieldsValue(getInitialValues())
  }, [state.show_user_popup])

  const saveUser = async (values) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'users.save' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'role' && Array.isArray(value)) {
        value.forEach((id) => formData.append('role', id))
      } else if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    const res = await postRequest(`/api/users/save_user/`, formData)

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    if (res?.status && Object.keys(res?.errors || {}).length === 0) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: selected?.email
              ? 'users.updateSuccess'
              : 'users.saveSuccess',
          }),
        },
      })
      props.getUserListData()
      closeFormPopup()
    } else {
      const errors =
        res?.errors && Object.keys(res.errors).length > 0
          ? Object.values(res.errors).flat().join(', ')
          : intl.formatMessage({ id: 'users.saveError' })
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: errors,
        },
      })
    }
  }

  return (
    <Modal
      open={state.show_user_popup}
      title={
        selected?.email
          ? intl.formatMessage({ id: 'users.updateTitle' })
          : intl.formatMessage({ id: 'users.createTitle' })
      }
      width="50%"
      className="users-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="user-form"
          name="user-form"
          form={form}
          layout="vertical"
          onFinish={saveUser}
        >
          <InputText name="is_create" hidden={true} isRequired={false} />
          <InputText name="is_active" hidden={true} isRequired={false} />
          <Row gutter={[16, 16]}>
            <Col span={!selected?.email && !navState?.make ? 12 : 24}>
              <InputText
                name="email"
                label={intl.formatMessage({ id: 'users.email' })}
                type="text"
                isRequired={true}
                errorHelp={true}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve()
                      const isEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
                        value
                      )
                      const isUuid =
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
                          value
                        )
                      if (isEmail || isUuid) return Promise.resolve()
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({ id: 'users.emailInvalid' })
                        )
                      )
                    },
                  },
                ]}
              />
            </Col>
            {!selected?.email && !navState?.make && (
              <Col span={12}>
                <SelectInput
                  name="make"
                  label={intl.formatMessage({ id: 'users.make' })}
                  options={makeOptions}
                  placeholder={intl.formatMessage({ id: 'users.make' })}
                  isRequired={false}
                  allowClear={true}
                  showSearch={true}
                />
              </Col>
            )}
            <Col span={12}>
              <InputText
                name="first_name"
                label={intl.formatMessage({ id: 'users.first_name' })}
                isRequired={true}
              />
            </Col>
            <Col span={12}>
              <InputText
                name="last_name"
                label={intl.formatMessage({ id: 'users.last_name' })}
                isRequired={false}
              />
            </Col>
            {!selected?.email && (
              <Col span={12}>
                <InputText
                  name="password"
                  label={intl.formatMessage({ id: 'users.password' })}
                  type="password"
                  isRequired={true}
                />
              </Col>
            )}
            <Col span={!selected?.email ? 12 : 24}>
              <SelectInput
                name="role"
                label={intl.formatMessage({ id: 'users.selectRole' })}
                options={roleOptions}
                placeholder={intl.formatMessage({ id: 'users.selectRole' })}
                isMulti={true}
                isRequired={canChangeRole}
                disabled={!canChangeRole}
                allowClear={true}
                showSearch={true}
                maxTagCount={5}
              />
            </Col>
            <Col span={24}>
              <Flex justify="end" className="py-4">
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'users.save' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default UserFormModal
